import { forwardRef } from "react";
import Chip from "@mui/material/Chip";
import type { ChipProps } from "@mui/material/Chip";
import { styled } from "@mui/material";
import useMergeProps from "hooks/useMergeProps";

const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: theme.spacing(0.5),
  cursor: "default",
  fontFamily: theme.typography.fontFamily,
  padding: theme.spacing(0.25, 0.75),
  height: "auto",
  backgroundColor: theme.palette.grey[40],

  "&.MuiChip-clickable:hover": {
    cursor: "pointer",
  },

  "&.MuiChip-colorSuccess": {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
  },

  "& .MuiChip-label": {
    ...theme.typography.TextS,
    fontWeight: theme.typography.fontWeightMedium,
    paddingLeft: theme.spacing(0.25),
    paddingRight: theme.spacing(0.25),
  },

  "& .MuiSvgIcon-root": {
    color: "inherit",
    fontSize: "12px",
    marginLeft: theme.spacing(0),
    marginRight: theme.spacing(0.25),
  },

  "&[data-size='small']": {
    padding: theme.spacing(0, 0.5),
    "& .MuiChip-label": {
      ...theme.typography.TextXS,
      fontWeight: theme.typography.fontWeightMedium,
    },
    "& .MuiSvgIcon-root": {
      fontSize: "12px",
    },
  },
  "&[data-size='large']": {
    padding: theme.spacing(0.5, 1),
    "& .MuiChip-label": {
      ...theme.typography.TextM,
      fontWeight: theme.typography.fontWeightMedium,
    },
    "& .MuiSvgIcon-root": {
      fontSize: "16px",
    },
  },
  "&[data-size='extraLarge']": {
    padding: theme.spacing(0.5, 1),
    "& .MuiChip-label": {
      ...theme.typography.TextL,
      fontWeight: theme.typography.fontWeightMedium,
    },
    "& .MuiSvgIcon-root": {
      fontSize: "16px",
    },
  },
}));

const DEFAULT_PROPS = {
  size: "medium",
} as const;

interface TagProps extends Omit<ChipProps, "label" | "children" | "size"> {
  children?: ChipProps["label"];
  size?: "small" | "medium" | "large" | "extraLarge";
}

const Tag = forwardRef<HTMLDivElement, TagProps>((props, ref) => {
  const { children, size, ...rest } = useMergeProps(DEFAULT_PROPS, props);
  return <StyledChip ref={ref} label={children} data-size={size} {...rest} />;
});

export default Tag;
